import { useState } from 'react';
import { message } from 'antd';

interface EnterpriseDetailModalProps {
  visible: boolean;
  data: Record<string, any> | null;
  onClose: () => void;
}

const FIELD_GROUPS = [
  {
    title: '基本信息',
    icon: 'ri-information-line',
    fields: [
      ['qymc', '企业名称'],
      ['djzt', '登记状态'],
      ['fddbr', '法定代表人'],
      ['zczb', '注册资本'],
      ['clsj', '成立时间'],
      ['yyqx', '营业期限'],
      ['qylx', '企业类型'],
      ['qygm', '企业规模'],
    ],
  },
  {
    title: '注册信息',
    icon: 'ri-file-list-3-line',
    fields: [
      ['tyshxydm', '统一社会信用代码'],
      ['zch', '注册号'],
      ['zzjgdm', '组织机构代码'],
      ['nsrsbh', '纳税人识别号'],
    ],
  },
  {
    title: '联系方式',
    icon: 'ri-phone-line',
    fields: [
      ['yxsjh', '有效手机号'],
      ['gddh', '固定电话'],
      ['yx', '邮箱'],
      ['gwwz', '官方网站'],
    ],
  },
  {
    title: '地址信息',
    icon: 'ri-map-pin-line',
    fields: [
      ['zcdz', '注册地址'],
      ['sssf', '所属省份'],
      ['sscs', '所属城市'],
      ['ssqx', '所属区县'],
      ['txdz', '通讯地址'],
    ],
  },
  {
    title: '行业分类',
    icon: 'ri-price-tag-3-line',
    fields: [
      ['gbhyml', '国标行业门类'],
      ['gbhydl', '国标行业大类'],
      ['gbhyzl', '国标行业中类'],
      ['gbhyxl', '国标行业小类'],
    ],
  },
  {
    title: '参保信息',
    icon: 'ri-user-heart-line',
    fields: [
      ['cbrs', '参保人数'],
      ['cbrsssnb', '参保年报'],
    ],
  },
];

export default function EnterpriseDetailModal({ visible, data, onClose }: EnterpriseDetailModalProps) {
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [subscribeType, setSubscribeType] = useState('once');
  const [deliveryChannels, setDeliveryChannels] = useState<string[]>(['wechat']);

  const subscribeOptions = [
    { value: 'once', label: '单次推送', desc: '仅推送本次查询结果' },
    { value: 'daily', label: '每日推送', desc: '每日定时推送最新数据' },
    { value: 'weekly', label: '每周推送', desc: '每周一推送汇总报告' },
  ];

  const deliveryOptions = [
    { value: 'wechat', label: '微信', icon: 'ri-wechat-line', color: 'text-green-500' },
    { value: 'email', label: '邮箱', icon: 'ri-mail-line', color: 'text-blue-500' },
    { value: 'sms', label: '手机短信', icon: 'ri-smartphone-line', color: 'text-orange-500' },
  ];

  const estimatedTokens = 3000 * deliveryChannels.length;

  const toggleChannel = (ch: string) => {
    setDeliveryChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  if (!visible || !data) return null;

  const fmt = (val: any): string => {
    if (val === null || val === undefined || val === '') return '-';
    return String(val);
  };

  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-[860px] max-h-[65vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white rounded-t-xl">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-building-4-line text-blue-500 text-base" />
            <h3 className="text-sm font-semibold text-slate-700 truncate">{fmt(data.qymc)}</h3>
            {data.djzt && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                data.djzt === '在业' || data.djzt === '存续' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}>
                {data.djzt}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPushModalOpen(true)}
              className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors cursor-pointer flex items-center gap-1 mr-2"
            >
              <i className="ri-send-plane-line text-[13px]" />
              商机推送
            </button>
            <button
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* 经营范围 */}
          {data.jjfw && (
            <div className="bg-amber-50 rounded-lg px-3 py-2.5 border border-amber-100">
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-briefcase-line text-amber-500 text-[13px]" />
                <span className="text-[11px] font-semibold text-amber-700">经营范围</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">{data.jjfw}</p>
            </div>
          )}

          {FIELD_GROUPS.map((group) => {
            const hasValue = group.fields.some(([key]) => data[key] != null && data[key] !== '');
            if (!hasValue) return null;
            return (
              <div key={group.title}>
                <div className="flex items-center gap-1.5 mb-2">
                  <i className={`${group.icon} text-blue-400 text-[13px]`} />
                  <span className="text-xs font-semibold text-slate-600">{group.title}</span>
                </div>
                <div className="bg-slate-50 rounded-lg px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {group.fields.map(([key, label]) => {
                    const val = data[key];
                    if (val == null || val === '') return null;
                    return (
                      <div key={key} className="flex items-baseline gap-1.5 py-0.5">
                        <span className="text-[11px] text-slate-400 flex-shrink-0">{label}</span>
                        <span className="text-[11px] text-slate-700 font-medium truncate" title={fmt(val)}>{fmt(val)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* 企业简介 */}
          {data.qyjj && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-article-line text-blue-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">企业简介</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed bg-slate-50 rounded-lg px-3 py-2.5">{data.qyjj}</p>
            </div>
          )}
        </div>
      </div>

      {/* === 商机推送弹窗 === */}
      {pushModalOpen && (
        <div className="fixed inset-0 z-[2147483647] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setPushModalOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-[480px] max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-2">
                <i className="ri-send-plane-line text-blue-500 text-base" />
                <h3 className="text-sm font-semibold text-slate-700">商机推送</h3>
              </div>
              <button
                onClick={() => setPushModalOpen(false)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-loop-left-line text-blue-400 text-[13px]" />
                  <span className="text-xs font-semibold text-slate-600">订阅方式</span>
                </div>
                <div className="space-y-2">
                  {subscribeOptions.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                        subscribeType === opt.value
                          ? 'border-blue-400 bg-blue-50/50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="subscribeType"
                        value={opt.value}
                        checked={subscribeType === opt.value}
                        onChange={e => setSubscribeType(e.target.value)}
                        className="mt-0.5 accent-blue-500"
                      />
                      <div>
                        <div className="text-xs font-medium text-slate-700">{opt.label}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-share-line text-blue-400 text-[13px]" />
                  <span className="text-xs font-semibold text-slate-600">发送方式</span>
                </div>
                <div className="flex gap-2">
                  {deliveryOptions.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border cursor-pointer transition-all ${
                        deliveryChannels.includes(opt.value)
                          ? 'border-blue-400 bg-blue-50/50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={deliveryChannels.includes(opt.value)}
                        onChange={() => toggleChannel(opt.value)}
                        className="hidden"
                      />
                      <i className={`${opt.icon} ${opt.color} text-lg`} />
                      <span className="text-xs text-slate-600">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg px-4 py-3 border border-amber-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-coin-line text-amber-500 text-[13px]" />
                    <span className="text-xs font-medium text-amber-700">预计消耗 Token</span>
                  </div>
                  <span className="text-sm font-bold text-amber-600">{estimatedTokens.toLocaleString()}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-amber-500">
                  1 条数据 × {deliveryChannels.length} 个渠道 × 3,000 token/条
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-3">
              <button
                onClick={() => setPushModalOpen(false)}
                className="flex-1 py-2 rounded-lg border border-slate-300 bg-white text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setPushModalOpen(false);
                  message.success('推送成功');
                }}
                className="flex-1 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-send-plane-line text-base" />
                确认推送
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
